import React, { useState } from "react";

import Template from "@/components/guides/template";
import BreadCrumbs from "@/components/shared/bread-crumbs/BreadCrumbs";
import { routesURL } from "@/components/urls";
import Link from "next/link";
import useTitle from "@/components/hooks/useTitle";
import Footer from "@/components/landing-page/Footer";

const DataSourceGuide = () => {
  useTitle("Data Source Guide");
  const options = [
    { value: "csv", label: "CSV", icon: "/images/csv.svg" },
    { value: "excel", label: "Excel", icon: "/images/excel.png" },
  ];
  const [select, setSelect] = useState(options[0]);

  const onSelectChange = (e) => setSelect(e);
  const guideStepsExcel = [
    {
      title: "❌ Incorrect Excel file format",
      Body: () => (
        <div className="text-black-1">
          Incorrect excel data formatting can lead to difficulties in data
          processing. Here are some specific formatting tips for Excel files:
          <ul className=" flex flex-col gap-y-5 mt-3">
            <li style={{ listStyle: "inside" }}>
              Every row in your Excel file should have a corresponding column
              name in the header row.{" "}
            </li>
            <li style={{ listStyle: "inside" }}>
              Do not merge two Excel sheets into one. This will cause file
              upload error.
            </li>
            <li style={{ listStyle: "inside" }}>
              Rows with no column names will cause file upload error.
            </li>
            <li style={{ listStyle: "inside" }}>
              For each value in row there should be a column associated with it.
              Mismatch in number of columns and row data will cause file upload
              error.
            </li>
          </ul>
        </div>
      ),
      image: "/images/guides/Excel-02.png",
    },
    {
      title: "✅ Correct Excel file format",
      Body: () => (
        <div className=" text-black-1 ">
          Proper formatting of Excel data is crucial for ensuring that the data
          is usable and ready for analysis. Here are important guidelines to
          follow when uploading Excel files:
          <ul className=" flex flex-col gap-y-5 mt-3 ">
            <li style={{ listStyle: "inside" }}>
              Always include a header row at the top of each worksheet.
            </li>
            <li style={{ listStyle: "inside" }}>
              This row should contain clear, descriptive titles for each column,
              which are essential for identifying appropriate data types.
            </li>
            <li style={{ listStyle: "inside" }}>
              Ensure that all data within the file adheres to a uniform format
              suitable for its type.
            </li>
          </ul>
        </div>
      ),
      image: "/images/guides/Excel-01.png",
    },
  ];

  const guideStepsCSV = [
    {
      title: "❌ Incorrect CSV file format",
      Body: () => (
        <div className="text-black-1">
          Incorrect CSV data formatting can lead to significant problems during
          data analysis. Here are some specific formatting tips for CSV:
          <ul className=" flex flex-col gap-y-5 mt-3  ">
            <li style={{ listStyle: "inside" }}>
              A header row must be present in the file.
            </li>
            <li style={{ listStyle: "inside" }}>
              Every row in your CSV file should have a corresponding column name
              in the header row.
            </li>
            <li style={{ listStyle: "inside" }}>
              {" "}
              For each value in row there should be a column associated with it.
              Mismatch in number of columns and row data will cause file upload
              error.
            </li>
            <li style={{ listStyle: "inside" }}>
              Data type of each column should be appropriate for successfully
              executing SQL queries.
            </li>
          </ul>
        </div>
      ),
      image: "/images/guides/CSV-02.png",
    },
    {
      title: "✅ Correct CSV file format",
      Body: () => (
        <div className="text-black-1">
          To ensure that your CSV files are correctly formatted for efficient
          processing and analysis, follow these guidelines:
          <ul className=" flex flex-col gap-y-5 mt-3  ">
            <li style={{ listStyle: "inside" }}>
              The very first line of your CSV file should always be the header
              row.
            </li>
            <li style={{ listStyle: "inside" }}>
              {" "}
              This row must contain the names of the columns, clearly describing
              the data each column holds.
            </li>
            <li style={{ listStyle: "inside" }}>
              Each row in your CSV file should use a comma to separate
              individual fields.
            </li>
            <li style={{ listStyle: "inside" }}>
              Ensure that the data in each row adheres to consistent and correct
              data formats.
            </li>
          </ul>
        </div>
      ),
      image: "/images/guides/CSV-01.png",
    },
  ];

  const types = {
    select,
    options,
    onSelectChange,
  };
  return (
    <>
      <div className=" mx-auto max-w-[900px] max-sm:px-6">
        <BreadCrumbs>
          <Link href={routesURL.guides}>Guides</Link>
          <div>Data Source Guide</div>
        </BreadCrumbs>
      </div>
      <Template
        guideSteps={select.value === "csv" ? guideStepsCSV : guideStepsExcel}
        types={types}
        title={"Data Source Guide"}
        key={select.value}
      />
      <Footer />
    </>
  );
};

export default DataSourceGuide;
